import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Camera, Crown, Sparkles, Wand2, Droplets, Video, Image as ImageIcon, Star, ArrowRight, Palette } from "lucide-react";
import { useTranslation } from "react-i18next";
import SEO from "../components/SEO";
import OnRequestFleet from "../components/OnRequestFleet";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.15, ease: [0.16, 1, 0.3, 1] },
  }),
};

const morganImages = {
  main: "https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/4tmat5ex_P1000566.webp",
  detail: "https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/rizwtbh1_P1000584.webp",
  city: "https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/hwov4wed_P1000576.webp",
  road: "https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/70l8o4yf_P1000563.webp",
};

const SIGNATURE_ICONS = [Wand2, Droplets, Video, ImageIcon, Star, Sparkles];

export default function ServicesPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const pillar1Tags = t("services.pillar1_tags", { returnObjects: true });
  const pillar2Tags = t("services.pillar2_tags", { returnObjects: true });
  const pillar3Tags = t("services.pillar3_tags", { returnObjects: true });
  const process = t("services.process", { returnObjects: true });
  const signatureItems = t("services.signature_items", { returnObjects: true });

  useEffect(() => {
    if (location.hash === "#fleet") {
      const el = document.getElementById("fleet");
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
      }
    }
  }, [location]);

  return (
    <div data-testid="services-page">
      <SEO titleKey="seo.services_title" descriptionKey="seo.services_description" path="/services" />
      {/* Header */}
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-20" style={{ backgroundColor: "#FAF9F6" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <p
              className="text-xs uppercase tracking-[0.2em] text-[#C4A462] mb-4"
              style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
            >
              {t("services.eyebrow")}
            </p>
          </motion.div>
          <motion.h1
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={1}
            className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-[#1A1B1A] max-w-4xl"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}
          >
            {t("services.hero_1")} <span style={{ fontWeight: 600 }}>{t("services.hero_iconiche")}</span> {t("services.hero_2")} <span style={{ fontWeight: 600 }}>{t("services.hero_straordinari")}</span>
          </motion.h1>
        </div>
      </section>

      {/* Pillars */}
      <section data-testid="three-pillars" className="pb-24 lg:pb-32" style={{ backgroundColor: "#FAF9F6" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.p
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-lg sm:text-xl lg:text-2xl text-[#1A1B1A] leading-relaxed max-w-3xl mb-16 lg:mb-20"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}
          >
            {t("services.intro")}
          </motion.p>

          {/* Pillar 1: Creative Production */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            data-testid="pillar-creative-production"
            className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start mb-20 lg:mb-28 pb-16 border-b border-[#D8D4CC]/40"
          >
            <div className="lg:col-span-2 flex lg:flex-col items-start gap-4">
              <p className="text-6xl lg:text-7xl text-[#D8D4CC]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}>01</p>
              <Camera strokeWidth={1} size={28} className="text-[#C4A462] lg:mt-4" />
            </div>
            <div className="lg:col-span-6">
              <p className="text-xs uppercase tracking-[0.2em] text-[#C4A462] mb-3" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                {t("services.pillar1_label")}
              </p>
              <h2 className="text-3xl sm:text-4xl tracking-tighter text-[#1A1B1A] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
                {t("services.pillar1_title")}
              </h2>
              <p className="text-sm text-[#4A4A4A] leading-relaxed mb-4" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
                {t("services.pillar1_desc")}
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-wrap gap-2 items-start">
              {pillar1Tags.map((tag) => (
                <span key={tag} className="text-[10px] uppercase tracking-[0.2em] text-[#4A4A4A] border border-[#D8D4CC] px-3 py-1.5"
                  style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                  {tag}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Pillar 2: Luxury Events */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            data-testid="pillar-luxury-events"
            className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start mb-20 lg:mb-28 pb-16 border-b border-[#D8D4CC]/40"
          >
            <div className="lg:col-span-2 flex lg:flex-col items-start gap-4">
              <p className="text-6xl lg:text-7xl text-[#D8D4CC]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}>02</p>
              <Crown strokeWidth={1} size={28} className="text-[#C4A462] lg:mt-4" />
            </div>
            <div className="lg:col-span-6">
              <p className="text-xs uppercase tracking-[0.2em] text-[#C4A462] mb-3" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                {t("services.pillar2_label")}
              </p>
              <h2 className="text-3xl sm:text-4xl tracking-tighter text-[#1A1B1A] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
                {t("services.pillar2_title")}
              </h2>
              <p className="text-sm text-[#4A4A4A] leading-relaxed mb-4" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
                {t("services.pillar2_desc")}
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-wrap gap-2 items-start">
              {pillar2Tags.map((tag) => (
                <span key={tag} className="text-[10px] uppercase tracking-[0.2em] text-[#4A4A4A] border border-[#D8D4CC] px-3 py-1.5"
                  style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                  {tag}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Pillar 3: Consulenza estetica e scenografica */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            data-testid="pillar-consulenza-estetica"
            className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start mb-20 lg:mb-28 pb-16 border-b border-[#D8D4CC]/40"
          >
            <div className="lg:col-span-2 flex lg:flex-col items-start gap-4">
              <p className="text-6xl lg:text-7xl text-[#D8D4CC]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}>03</p>
              <Palette strokeWidth={1} size={28} className="text-[#C4A462] lg:mt-4" />
            </div>
            <div className="lg:col-span-6">
              <p className="text-xs uppercase tracking-[0.2em] text-[#C4A462] mb-3" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                {t("services.pillar3_label")}
              </p>
              <h2 className="text-3xl sm:text-4xl tracking-tighter text-[#1A1B1A] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
                {t("services.pillar3_title")}
              </h2>
              <p className="text-sm text-[#4A4A4A] leading-relaxed mb-4 italic" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: "1.05rem" }}>
                {t("services.pillar3_lead")}
              </p>
              <p className="text-sm text-[#4A4A4A] leading-relaxed mb-4" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
                {t("services.pillar3_desc1")}
              </p>
              <p className="text-sm text-[#4A4A4A] leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
                {t("services.pillar3_desc2")}
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-wrap gap-2 items-start">
              {pillar3Tags.map((tag) => (
                <span key={tag} className="text-[10px] uppercase tracking-[0.2em] text-[#4A4A4A] border border-[#D8D4CC] px-3 py-1.5"
                  style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                  {tag}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Pillar 4: ETERNA Su Misura — HERO PILLAR */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            data-testid="pillar-su-misura"
            className="relative overflow-hidden grain-overlay"
            style={{ backgroundColor: "#1A1B1A" }}
          >
            <div className="relative z-10 px-8 lg:px-16 py-16 lg:py-24">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
                <div className="lg:col-span-2 flex lg:flex-col items-start gap-4">
                  <p className="text-6xl lg:text-7xl text-[#C4A462]/30" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}>04</p>
                  <Sparkles strokeWidth={1} size={32} className="text-[#C4A462] lg:mt-4" />
                </div>
                <div className="lg:col-span-10">
                  <p
                    className="text-xs uppercase tracking-[0.25em] text-[#C4A462] mb-4"
                    style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
                  >
                    {t("services.pillar4_label")}
                  </p>
                  <h2
                    className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-[#FAF9F6] mb-3 leading-[1.05]"
                    style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}
                  >
                    {t("services.pillar4_title_1")} <span style={{ fontWeight: 600, fontStyle: "italic" }}>{t("services.pillar4_title_2")}</span>
                  </h2>
                  <p
                    className="text-lg sm:text-xl text-[#C4A462] mb-10 italic"
                    style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}
                  >
                    {t("services.pillar4_subtitle")}
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-12 items-start">
                    <div className="md:col-span-7 space-y-5" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
                      <p
                        className="text-base text-[#FAF9F6] leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: t("services.pillar4_p1") }}
                      />
                      <p className="text-sm text-[#A8A8A8] leading-relaxed">
                        {t("services.pillar4_p2")}
                      </p>
                      <p className="text-sm text-[#A8A8A8] leading-relaxed">
                        {t("services.pillar4_p3")}
                      </p>
                    </div>

                    <div className="md:col-span-5 space-y-4">
                      <p
                        className="text-[10px] uppercase tracking-[0.25em] text-[#C4A462] mb-4"
                        style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
                      >
                        {t("services.process_label")}
                      </p>
                      {process.map((s) => (
                        <div key={s.step} className="flex gap-4 pb-3 border-b border-[#FAF9F6]/10 last:border-0">
                          <p className="text-[#C4A462] text-2xl" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}>{s.step}</p>
                          <div>
                            <p className="text-[#FAF9F6] text-sm" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>{s.label}</p>
                            <p className="text-[#A8A8A8] text-xs mt-1" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>{s.text}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Link to="/contact" data-testid="su-misura-cta" className="inline-block mt-12">
                    <button className="bg-[#C4A462] text-[#1A1B1A] px-10 py-4 text-xs uppercase tracking-[0.15em] hover:bg-[#FAF9F6] transition-colors duration-300 flex items-center gap-3"
                      style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                      {t("services.pillar4_cta")} <ArrowRight strokeWidth={1.25} size={16} />
                    </button>
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Signature Details */}
      <section data-testid="signature-details-section" className="py-24 lg:py-32 grain-overlay" style={{ backgroundColor: "#F3F1EC" }}>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mb-16 max-w-3xl"
          >
            <p
              className="text-xs uppercase tracking-[0.2em] text-[#C4A462] mb-4"
              style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
            >
              {t("services.signature_eyebrow")}
            </p>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-[#1A1B1A] mb-6"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}
            >
              {t("services.signature_title_1")} <span style={{ fontWeight: 600 }}>{t("services.signature_title_2")}</span>
            </h2>
            <p
              className="text-sm text-[#4A4A4A] leading-relaxed"
              style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
            >
              {t("services.signature_intro")}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#D8D4CC]/40">
            {signatureItems.map((item, idx) => {
              const Icon = SIGNATURE_ICONS[idx] || Sparkles;
              return (
                <div
                  key={item.title}
                  data-testid={`signature-detail-${idx}`}
                  className="bg-[#FAF9F6] p-8 lg:p-10 hover:bg-white transition-colors duration-300"
                >
                  <Icon strokeWidth={1.25} size={28} className="text-[#C4A462] mb-6" />
                  <h3
                    className="text-xl lg:text-2xl tracking-tighter text-[#1A1B1A] mb-4 leading-tight"
                    style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
                  >
                    {item.title}
                  </h3>
                  <p
                    className="text-sm text-[#4A4A4A] leading-relaxed"
                    style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
                  >
                    {item.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Morgan 4/4 */}
      <section id="fleet" data-testid="car-morgan-section" className="py-24 lg:py-32 grain-overlay" style={{ backgroundColor: "#F3F1EC" }}>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mb-12"
          >
            <p
              className="text-xs uppercase tracking-[0.2em] text-[#C4A462] mb-4"
              style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
            >
              {t("services.fleet_eyebrow")}
            </p>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-[#1A1B1A]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}
            >
              Morgan <span style={{ fontWeight: 600 }}>4/4 Series II</span> <span className="text-[#757575] text-2xl lg:text-3xl">1958</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className="lg:col-span-7"
            >
              <div className="grid grid-cols-12 gap-3">
                <div className="col-span-12 img-hover-zoom">
                  <img src={morganImages.main} alt="Morgan 4/4 Series II" className="w-full h-64 lg:h-80 object-cover" />
                </div>
                <div className="col-span-6 img-hover-zoom">
                  <img src={morganImages.detail} alt="Morgan 4/4 dettaglio" className="w-full h-40 lg:h-52 object-cover" />
                </div>
                <div className="col-span-6 img-hover-zoom">
                  <img src={morganImages.city} alt="Morgan 4/4 in citta" className="w-full h-40 lg:h-52 object-cover" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={1}
              className="lg:col-span-5"
            >
              <p
                className="text-sm text-[#4A4A4A] leading-relaxed"
                style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
              >
                {t("services.morgan_desc")}
              </p>
              <div className="luxury-divider mt-8" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* On Request — Partner fleet */}
      <OnRequestFleet background="#FAF9F6" />

      {/* Closing CTA */}
      <section data-testid="services-closing-cta" className="py-24 lg:py-32 grain-overlay" style={{ backgroundColor: "#FAF9F6" }}>
        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <div className="luxury-divider mx-auto mb-8" />
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-[#1A1B1A] mb-8 leading-[1.1]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}
            >
              {t("services.closing_1")} <span style={{ fontWeight: 600, fontStyle: "italic" }}>{t("services.closing_2")}</span>
            </h2>
            <p
              className="text-sm text-[#4A4A4A] leading-relaxed mb-12 max-w-xl mx-auto"
              style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
            >
              {t("services.closing_sub")}
            </p>
            <Link to="/contact" data-testid="services-closing-cta-btn">
              <button
                className="btn-luxury flex items-center gap-3 mx-auto"
              >
                {t("services.closing_cta")} <ArrowRight strokeWidth={1.25} size={16} />
              </button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
